import React, { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import { useField } from 'react-final-form';

import { DefaultScreenDetailsSection } from './default-screen-details-section.component';
import { DefaultScreenImagesSection } from './default-screen-images-section.component';
import { TargetedKiosksSection } from './targeted-kiosks-section.component';

import { SelectTargetSection } from '../select-target-section.component';
import { KioskSelectionDialog } from '../kiosk-selection-dialog.component';

type DefaultScreenFormProps = {
  isEditMode: boolean;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  defaultScreenId?: string;
};

export const DefaultScreenForm = ({
  isEditMode,
  handleSubmit,
  defaultScreenId,
}: DefaultScreenFormProps) => {
  const [isKioskDialogOpen, setIsKioskDialogOpen] = useState(false);
  const [kioskIdsInputValue, setKioskIdsInputValue] = useState('');
  const hasInitializedKioskIds = useRef(false);

  const kioskIdsField = useField<string[]>('kioskIds', { subscription: { value: true } });
  const kioskIds = kioskIdsField.input.value || [];

  useEffect(() => {
    if (!hasInitializedKioskIds.current && kioskIds.length > 0) {
      hasInitializedKioskIds.current = true;
      setKioskIdsInputValue(kioskIds.join(', '));
    }
  }, [kioskIds]);

  const handleOpenKioskDialog = useCallback(() => {
    const ids = kioskIdsInputValue
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    kioskIdsField.input.onChange(ids);
    setIsKioskDialogOpen(true);
  }, [kioskIdsInputValue, kioskIdsField]);

  const handleCloseKioskDialog = useCallback(() => {
    setIsKioskDialogOpen(false);
  }, []);

  const handleKioskSelection = useCallback(
    (selectedIds: string[]) => {
      kioskIdsField.input.onChange(selectedIds);
      setKioskIdsInputValue(selectedIds.join(', '));
    },
    [kioskIdsField],
  );

  const handleKioskIdsInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setKioskIdsInputValue(event.target.value);
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <DefaultScreenDetailsSection />
        <DefaultScreenImagesSection />
        <SelectTargetSection
          displayValue={kioskIdsInputValue}
          onOpenKioskDialog={handleOpenKioskDialog}
          onChange={handleKioskIdsInputChange}
        />
        {isEditMode && <TargetedKiosksSection defaultScreenId={defaultScreenId} />}
      </form>

      <KioskSelectionDialog
        open={isKioskDialogOpen}
        onClose={handleCloseKioskDialog}
        onConfirm={handleKioskSelection}
        initialKioskIds={kioskIds}
      />
    </>
  );
};
